/* Glow Island — theme.js (no dependencies) */
(() => {
  const G = window.GlowIsland || {};
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const isRTL = () => document.documentElement.dir === 'rtl';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animOn = document.documentElement.classList.contains('anim-on') && !reduceMotion;

  /* ---------- Money ---------- */
  function formatMoney(cents, format = G.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = (n, d = 2, t = ',', dec = '.') => {
      if (isNaN(n) || n == null) return '0';
      n = (n / 100).toFixed(d);
      const p = n.split('.');
      return p[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t) + (p[1] ? dec + p[1] : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        default: return fmt(cents, 2);
      }
    });
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(msg) {
    const el = $('.toast');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }

  /* ---------- Page loader ---------- */
  const markLoaded = () => document.body.classList.add('is-loaded');
  window.addEventListener('load', () => setTimeout(markLoaded, 250));
  setTimeout(markLoaded, 2500);
  window.addEventListener('pageshow', (e) => { if (e.persisted) markLoaded(); });

  /* ---------- Scroll reveal ---------- */
  function initReveal(root = document) {
    const els = $$('[data-animate], .reveal-clip', root).filter((el) => !el.classList.contains('is-visible'));
    if (!animOn || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Sparkles & bubbles ---------- */
  const SPARK = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0c.6 5.6 2.4 9 12 12-9.6 3-11.4 6.4-12 12-.6-5.6-2.4-9-12-12C9.6 9 11.4 5.6 12 0z"/></svg>';
  function initSparkles(root = document) {
    if (!animOn) return;
    $$('[data-sparkles]', root).forEach((host) => {
      if (host.dataset.sparklesReady) return;
      host.dataset.sparklesReady = '1';
      const layer = document.createElement('div');
      layer.className = 'sparkles';
      layer.setAttribute('aria-hidden', 'true');
      const count = parseInt(host.dataset.sparkles || '10', 10);
      for (let i = 0; i < count; i++) {
        const s = document.createElement('span');
        s.className = 'sparkle' + (i % 3 === 0 ? ' sparkle--gold' : '');
        s.innerHTML = SPARK;
        s.style.cssText = `top:${Math.random() * 90 + 3}%;inset-inline-start:${Math.random() * 94 + 2}%;--s:${(Math.random() * 1.4 + 0.8).toFixed(2)}rem;--t:${(Math.random() * 3 + 3).toFixed(2)}s;--delay:${(Math.random() * 5).toFixed(2)}s`;
        layer.appendChild(s);
      }
      if (host.hasAttribute('data-bubbles')) {
        for (let i = 0; i < 8; i++) {
          const b = document.createElement('span');
          const size = Math.random() * 3.6 + 1.2;
          b.className = 'bubble';
          b.style.cssText = `inset-inline-start:${Math.random() * 96}%;width:${size}rem;height:${size}rem;--t:${(Math.random() * 10 + 10).toFixed(1)}s;--delay:${(-Math.random() * 18).toFixed(1)}s`;
          layer.appendChild(b);
        }
      }
      host.prepend(layer);
    });
  }

  /* ---------- Cursor glow ---------- */
  function initCursorGlow() {
    if (!animOn || !document.body.hasAttribute('data-cursor-glow') || window.matchMedia('(hover: none)').matches) return;
    const g = document.createElement('div');
    g.className = 'cursor-glow';
    document.body.appendChild(g);
    let x = 0, y = 0, cx = 0, cy = 0, raf;
    const loop = () => { cx += (x - cx) * 0.12; cy += (y - cy) * 0.12; g.style.transform = `translate(${cx - g.offsetWidth / 2}px, ${cy - g.offsetHeight / 2}px)`; raf = requestAnimationFrame(loop); };
    window.addEventListener('pointermove', (e) => { x = e.clientX; y = e.clientY; g.classList.add('is-active'); if (!raf) loop(); }, { passive: true });
    document.addEventListener('pointerleave', () => g.classList.remove('is-active'));
  }

  /* ---------- Parallax ---------- */
  function initParallax() {
    if (!animOn) return;
    const els = $$('[data-parallax]');
    if (!els.length) return;
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const speed = parseFloat(el.dataset.parallax) || 0.15;
        const offset = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const wrap = $('.header-wrapper');
    if (!wrap) return;
    const hideOnScroll = wrap.dataset.hideOnScroll === 'true';
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      wrap.classList.toggle('is-scrolled', y > 20);
      if (hideOnScroll) wrap.classList.toggle('is-hidden', y > last && y > 300 && !document.body.classList.contains('overflow-hidden'));
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Drawers & modals ---------- */
  let lastFocus = null;
  function openDrawer(id) {
    const d = document.getElementById(id);
    if (!d) return;
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('overflow-hidden');
    setTimeout(() => { const f = $('input[type="search"], .drawer__close, button, a', d); f && f.focus({ preventScroll: true }); }, 80);
  }
  function closeDrawer(d) {
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.classList.remove('overflow-hidden');
    lastFocus && lastFocus.focus && lastFocus.focus({ preventScroll: true });
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const id = opener.dataset.open;
      if (id === 'CartDrawer' && G.cartType !== 'drawer') return;
      e.preventDefault();
      openDrawer(id);
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) { e.preventDefault(); closeDrawer(closer.closest('.drawer, .search-modal')); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') $$('.drawer.is-open, .search-modal.is-open').forEach(closeDrawer);
  });

  /* ---------- Hero slideshow ---------- */
  function splitWords(el) {
    if (el.dataset.split) return;
    el.dataset.split = '1';
    const words = el.textContent.trim().split(/\s+/);
    el.innerHTML = words.map((w, i) => `<span class="word"><span style="--w:${i}">${w}</span></span>`).join(' ');
  }
  function initHero(root = document) {
    $$('[data-hero]', root).forEach((hero) => {
      const slides = $$('.hero__slide', hero);
      const dots = $$('.hero__dot', hero);
      $$('.hero__title', hero).forEach(splitWords);
      if (slides.length < 2) { slides[0] && slides[0].classList.add('is-active'); return; }
      let i = 0, timer;
      const delay = (parseInt(hero.dataset.speed, 10) || 6) * 1000;
      const go = (n) => {
        slides[i].classList.remove('is-active'); dots[i] && dots[i].classList.remove('is-active');
        i = (n + slides.length) % slides.length;
        slides[i].classList.add('is-active'); dots[i] && dots[i].classList.add('is-active');
      };
      const play = () => { if (hero.dataset.autoplay === 'true') { clearInterval(timer); timer = setInterval(() => go(i + 1), delay); } };
      dots.forEach((d, n) => d.addEventListener('click', () => { go(n); play(); }));
      hero.addEventListener('mouseenter', () => clearInterval(timer));
      hero.addEventListener('mouseleave', play);
      let sx = null;
      hero.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
      hero.addEventListener('touchend', (e) => {
        if (sx === null) return;
        const dx = e.changedTouches[0].clientX - sx;
        if (Math.abs(dx) > 50) go(i + ((dx < 0) !== isRTL() ? 1 : -1));
        sx = null; play();
      });
      play();
    });
  }

  /* ---------- Tabs ---------- */
  function initTabs(root = document) {
    $$('[data-tabs]', root).forEach((wrap) => {
      const btns = $$('[role="tab"]', wrap);
      const pill = $('.tabs__pill', wrap);
      const movePill = (btn) => {
        if (!pill || !btn) return;
        const parent = btn.parentElement.getBoundingClientRect();
        const r = btn.getBoundingClientRect();
        const start = isRTL() ? parent.right - r.right : r.left - parent.left;
        pill.style.insetInlineStart = start + 'px';
        pill.style.inlineSize = r.width + 'px';
      };
      const select = (btn) => {
        btns.forEach((b) => {
          const on = b === btn;
          b.setAttribute('aria-selected', on);
          b.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(b.getAttribute('aria-controls'));
          if (panel) {
            panel.hidden = !on;
            if (on) { panel.classList.remove('is-entering'); void panel.offsetWidth; panel.classList.add('is-entering'); }
          }
        });
        movePill(btn);
      };
      btns.forEach((b) => {
        b.addEventListener('click', () => select(b));
        b.addEventListener('keydown', (e) => {
          const idx = btns.indexOf(b);
          const next = (e.key === 'ArrowRight') !== isRTL() ? 1 : -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { const t = btns[(idx + next + btns.length) % btns.length]; t.focus(); select(t); }
        });
      });
      const active = btns.find((b) => b.getAttribute('aria-selected') === 'true') || btns[0];
      requestAnimationFrame(() => movePill(active));
      window.addEventListener('resize', () => movePill(btns.find((b) => b.getAttribute('aria-selected') === 'true')));
      document.fonts && document.fonts.ready.then(() => movePill(btns.find((b) => b.getAttribute('aria-selected') === 'true')));
    });
  }

  /* ---------- Sliders (prev/next buttons) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-slide]');
    if (!btn) return;
    const track = document.getElementById(btn.dataset.target);
    if (!track) return;
    const dir = btn.dataset.slide === 'next' ? 1 : -1;
    const amount = track.clientWidth * 0.8 * dir * (isRTL() ? -1 : 1);
    track.scrollBy({ left: amount, behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ---------- Quantity inputs ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-qty]');
    if (!b) return;
    const input = b.parentElement.querySelector('input');
    const min = parseInt(input.min || '0', 10);
    const v = Math.max(min, (parseInt(input.value, 10) || 0) + (b.dataset.qty === 'plus' ? 1 : -1));
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart ---------- */
  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
      el.classList.remove('is-bumped'); void el.offsetWidth; el.classList.add('is-bumped');
    });
  }
  async function refreshDrawer(sectionsHtml) {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    let html = sectionsHtml && sectionsHtml['cart-drawer'];
    if (!html) {
      const res = await fetch(`${G.routes.root.replace(/\/$/, '')}/?sections=cart-drawer`);
      html = (await res.json())['cart-drawer'];
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('.drawer__panel');
    const current = drawer.querySelector('.drawer__panel');
    if (fresh && current) current.innerHTML = fresh.innerHTML;
  }
  async function addToCart(form, button) {
    const fd = new FormData(form);
    if (G.cartType === 'drawer') fd.append('sections', 'cart-drawer');
    button && button.classList.add('is-loading');
    try {
      const res = await fetch(`${G.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
      const data = await res.json();
      if (!res.ok || data.status) { toast(data.description || data.message || G.strings.error); return; }
      const cart = await (await fetch(`${G.routes.cart}.js`)).json();
      updateCount(cart.item_count);
      if (G.cartType === 'drawer') {
        await refreshDrawer(data.sections);
        openDrawer('CartDrawer');
      } else {
        toast(G.strings.added);
      }
    } catch (err) {
      toast(G.strings.error);
    } finally {
      button && button.classList.remove('is-loading');
    }
  }
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form || G.cartType !== 'drawer' && !form.hasAttribute('data-force-ajax')) return;
    e.preventDefault();
    addToCart(form, form.querySelector('[type="submit"]'));
  });

  async function changeLine(line, quantity, row) {
    row && row.classList.add('is-removing');
    try {
      const res = await fetch(`${G.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-drawer'] })
      });
      const data = await res.json();
      if (!res.ok) { toast(data.description || G.strings.error); row && row.classList.remove('is-removing'); return; }
      updateCount(data.item_count);
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
      await refreshDrawer(data.sections);
    } catch (err) { toast(G.strings.error); row && row.classList.remove('is-removing'); }
  }
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    changeLine(parseInt(input.dataset.lineQty, 10), parseInt(input.value, 10), input.closest('.cart-item'));
  });
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-line-remove]');
    if (!rm) return;
    e.preventDefault();
    changeLine(parseInt(rm.dataset.lineRemove, 10), 0, rm.closest('.cart-item'));
  });
  let noteTimer;
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cart-note]')) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(`${G.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) });
    }, 500);
  });

  /* ---------- Product: variants & gallery ---------- */
  function initProduct(root = document) {
    $$('[data-product]', root).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const json = $('script[data-product-json]', wrap);
      const product = json ? JSON.parse(json.textContent) : null;
      const form = $('form[data-ajax-cart]', wrap);
      const idInput = form && $('input[name="id"]', form);
      const atc = form && $('[type="submit"]', form);
      const priceEl = $('[data-price]', wrap);
      const stockEl = $('[data-stock]', wrap);
      const gallery = $('[data-gallery]', wrap);

      const showMedia = (mediaId) => {
        if (!gallery) return;
        const slides = $$('.gallery__slide', gallery);
        const thumbs = $$('.gallery__thumb', gallery);
        let found = false;
        slides.forEach((s) => { const on = s.dataset.mediaId === String(mediaId); s.classList.toggle('is-active', on); if (on) found = true; });
        if (!found) return;
        thumbs.forEach((t) => {
          const on = t.dataset.mediaId === String(mediaId);
          t.classList.toggle('is-active', on);
          if (on) t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        });
      };
      if (gallery) {
        $$('.gallery__thumb', gallery).forEach((t) => t.addEventListener('click', () => showMedia(t.dataset.mediaId)));
        const main = $('.gallery__main', gallery);
        main && main.addEventListener('click', (e) => {
          if (!e.target.closest('img') || window.matchMedia('(hover: none)').matches) return;
          main.classList.toggle('is-zoomed');
        });
        main && main.addEventListener('mousemove', (e) => {
          if (!main.classList.contains('is-zoomed')) return;
          const img = $('.gallery__slide.is-active img', main);
          const r = main.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        });
        main && main.addEventListener('mouseleave', () => main.classList.remove('is-zoomed'));
        let sx = null;
        main && main.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
        main && main.addEventListener('touchend', (e) => {
          if (sx === null) return;
          const dx = e.changedTouches[0].clientX - sx;
          const thumbs = $$('.gallery__thumb', gallery);
          const cur = thumbs.findIndex((t) => t.classList.contains('is-active'));
          if (Math.abs(dx) > 40 && thumbs.length) {
            const step = (dx < 0) !== isRTL() ? 1 : -1;
            const n = thumbs[(cur + step + thumbs.length) % thumbs.length];
            showMedia(n.dataset.mediaId);
          }
          sx = null;
        });
      }

      if (!product || !form) return;
      const hasPickers = $$('[data-option-index]', wrap).length > 0;
      const onChange = () => {
        const selected = $$('[data-option-index]', wrap).map((fs) => {
          const c = $('input:checked', fs) || $('select', fs);
          return c ? c.value : null;
        });
        // Products without options (single "Default Title" variant) have no pickers:
        // use the variant already in the form instead of matching option values.
        const variant = hasPickers
          ? product.variants.find((v) => v.options.every((o, i) => o === selected[i]))
          : product.variants.find((v) => String(v.id) === String(idInput.value)) || product.variants[0];
        $$('[data-option-index]', wrap).forEach((fs) => {
          const label = $('[data-selected-value]', fs);
          const c = $('input:checked', fs);
          if (label && c) label.textContent = c.value;
        });
        // mark unavailable combos
        $$('[data-option-index]', wrap).forEach((fs) => {
          const idx = parseInt(fs.dataset.optionIndex, 10);
          $$('input', fs).forEach((inp) => {
            const test = selected.slice(); test[idx] = inp.value;
            const ok = product.variants.some((v) => v.available && v.options.every((o, i) => o === test[i]));
            inp.classList.toggle('is-unavailable', !ok);
          });
        });
        if (!variant) {
          atc.disabled = true; $('span', atc).textContent = G.strings.unavailable; return;
        }
        idInput.value = variant.id;
        atc.disabled = !variant.available;
        $('span', atc).textContent = variant.available ? G.strings.addToCart : G.strings.soldOut;
        if (priceEl) {
          priceEl.innerHTML = `<span>${formatMoney(variant.price)}</span>` + (variant.compare_at_price > variant.price ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : '');
        }
        if (stockEl) {
          const qty = variant.inventory_quantity;
          stockEl.hidden = !variant.available;
          if (variant.available && typeof qty === 'number' && qty > 0 && qty <= 5 && variant.inventory_management) {
            stockEl.className = 'stock-note stock-note--low';
            stockEl.textContent = stockEl.dataset.low.replace('[count]', qty);
          } else {
            stockEl.className = 'stock-note';
            stockEl.textContent = stockEl.dataset.in;
          }
        }
        if (variant.featured_media) showMedia(variant.featured_media.id);
        const sticky = $('[data-sticky-atc] [data-sticky-price]');
        if (sticky) sticky.textContent = formatMoney(variant.price);
        if (hasPickers) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      };
      wrap.addEventListener('change', (e) => { if (e.target.closest('[data-option-index]')) onChange(); });
      onChange();

      // Sticky add to cart
      const sticky = $('[data-sticky-atc]');
      if (sticky && atc && 'IntersectionObserver' in window) {
        new IntersectionObserver(([en]) => sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0)).observe(atc);
        $('button', sticky).addEventListener('click', () => { if (atc.disabled) { atc.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; } form.requestSubmit ? form.requestSubmit(atc) : atc.click(); });
      }
    });
  }

  /* ---------- Collection filters ---------- */
  function initFilters() {
    $$('form[data-filter-form]').forEach((form) => {
      let t;
      form.addEventListener('change', (e) => {
        if (e.target.type === 'number') return;
        clearTimeout(t);
        t = setTimeout(() => submitFilters(form), 250);
      });
      $$('input[type="number"]', form).forEach((inp) => inp.addEventListener('change', () => submitFilters(form)));
    });
    $$('[data-sort]').forEach((sel) => sel.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }));
  }
  function submitFilters(form) {
    const params = new URLSearchParams(new FormData(form));
    for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
    const sort = new URL(window.location.href).searchParams.get('sort_by');
    if (sort) params.set('sort_by', sort);
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  }

  /* ---------- Predictive search ---------- */
  function initSearch() {
    const modal = document.getElementById('SearchModal');
    if (!modal) return;
    const input = $('input[type="search"]', modal);
    const results = $('[data-predictive]', modal);
    const suggest = $('.search-suggest', modal);
    let t, ctrl;
    input.addEventListener('input', () => {
      clearTimeout(t);
      const q = input.value.trim();
      if (!q) { results.innerHTML = ''; suggest && (suggest.hidden = false); return; }
      t = setTimeout(async () => {
        ctrl && ctrl.abort();
        ctrl = new AbortController();
        try {
          const res = await fetch(`${G.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=6`, { signal: ctrl.signal });
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('#predictive-search-results');
          results.innerHTML = inner ? inner.innerHTML : '';
          suggest && (suggest.hidden = true);
        } catch (err) { /* aborted */ }
      }, 220);
    });
  }

  /* ---------- Theme editor support ---------- */
  function initAll(root = document) {
    initReveal(root);
    initSparkles(root);
    initHero(root);
    initTabs(root);
    initProduct(root);
  }
  document.addEventListener('shopify:section:load', (e) => { initAll(e.target); initReveal(e.target); $$('[data-animate]', e.target).forEach((el) => el.classList.add('is-visible')); });
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.hero__slide');
    if (slide) {
      const hero = slide.closest('[data-hero]');
      const idx = $$('.hero__slide', hero).indexOf(slide);
      const dot = $$('.hero__dot', hero)[idx];
      dot ? dot.click() : null;
    }
  });

  const boot = () => {
    initAll();
    initHeader();
    initCursorGlow();
    initParallax();
    initFilters();
    initSearch();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
